import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const CollaborationPanel = ({ collaborators, onCollaboratorUpdate }) => {
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');

  const mockCollaborators = [
    {
      id: 'user-1',
      name: 'Agent Sarah Johnson',
      email: 'sarah.johnson@agency.gov',
      role: 'Lead Investigator',
      avatar: 'https://randomuser.me/api/portraits/women/32.jpg',
      status: 'online',
      lastActive: '2025-01-15T10:30:00Z',
      permissions: ['edit', 'review', 'export'],
      sections: ['executive-summary', 'attack-timeline']
    },
    {
      id: 'user-2',
      name: 'Detective Mike Chen',
      email: 'mike.chen@police.gov',
      role: 'Technical Analyst',
      avatar: 'https://randomuser.me/api/portraits/men/45.jpg',
      status: 'online',
      lastActive: '2025-01-15T10:15:00Z',
      permissions: ['edit', 'review'],
      sections: ['fund-tracing', 'evidence']
    },
    {
      id: 'user-3',
      name: 'Dr. Emily Rodriguez',
      email: 'emily.rodriguez@forensics.org',
      role: 'Forensic Expert',
      avatar: 'https://randomuser.me/api/portraits/women/28.jpg',
      status: 'away',
      lastActive: '2025-01-15T09:45:00Z',
      permissions: ['review'],
      sections: ['methodology', 'conclusions']
    },
    {
      id: 'user-4',
      name: 'Legal Counsel',
      email: 'counsel@legal.gov',
      role: 'Legal Advisor',
      avatar: 'https://randomuser.me/api/portraits/men/52.jpg',
      status: 'offline',
      lastActive: '2025-01-14T16:20:00Z',
      permissions: ['review'],
      sections: ['legal-analysis']
    }
  ];

  const mockComments = [
    {
      id: 'comment-1',
      userId: 'user-2',
      section: 'fund-tracing',
      content: 'The mixing service analysis needs more detail about the specific techniques used.',
      timestamp: '2025-01-15T10:15:00Z',
      resolved: false
    },
    {
      id: 'comment-2',
      userId: 'user-3',
      section: 'executive-summary',
      content: 'Consider adding the total recovery potential to the summary.',
      timestamp: '2025-01-15T09:45:00Z',
      resolved: true
    },
    {
      id: 'comment-3',
      userId: 'user-4',
      section: 'legal-analysis',
      content: 'This section should reference the relevant cryptocurrency regulations.',
      timestamp: '2025-01-14T16:20:00Z',
      resolved: false
    }
  ];

  const getStatusColor = (status) => {
    const colors = {
      'online': 'bg-success',
      'away': 'bg-warning',
      'offline': 'bg-text-secondary'
    };
    return colors?.[status] || 'bg-text-secondary';
  };

  const getStatusText = (status) => {
    const texts = {
      'online': 'Online',
      'away': 'Away',
      'offline': 'Offline'
    };
    return texts?.[status] || 'Unknown';
  };

  const handleInviteCollaborator = () => {
    if (inviteEmail) {
      // Mock invite functionality
      console.log('Inviting:', inviteEmail);
      setInviteEmail('');
      setShowInviteForm(false);
    }
  };

  const unresolvedComments = mockComments?.filter(comment => !comment?.resolved);

  return (
    <div className="bg-surface rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-text-primary">Collaboration</h3>
          <p className="text-sm text-text-secondary mt-1">Manage team members and review feedback</p>
        </div>
        <Button
          variant="default"
          size="sm"
          onClick={() => setShowInviteForm(!showInviteForm)}
        >
          <Icon name="UserPlus" size={16} color="currentColor" className="mr-2" />
          Invite
        </Button>
      </div>
      {/* Invite Form */}
      {showInviteForm && (
        <div className="mb-6 p-4 bg-muted rounded-lg">
          <h4 className="font-medium text-text-primary mb-3">Invite Collaborator</h4>
          <div className="flex gap-3">
            <div className="flex-1">
              <Input
                type="email"
                placeholder="Enter email address"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e?.target?.value)}
              />
            </div>
            <Button variant="default" size="sm" onClick={handleInviteCollaborator}>
              Send Invite
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowInviteForm(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
      {/* Collaborators List */}
      <div className="space-y-4 mb-8">
        <h4 className="font-medium text-text-primary">Team Members ({mockCollaborators?.length})</h4>
        {mockCollaborators?.map((collaborator) => (
          <div key={collaborator?.id} className="flex items-center justify-between p-3 border border-border rounded-lg">
            <div className="flex items-center space-x-3">
              <div className="relative">
                <img
                  src={collaborator?.avatar}
                  alt={collaborator?.name}
                  className="w-10 h-10 rounded-full object-cover"
                />
                <div className={`absolute -bottom-1 -right-1 w-3 h-3 rounded-full border-2 border-surface ${getStatusColor(collaborator?.status)}`} />
              </div>
              <div>
                <div className="flex items-center space-x-2">
                  <h5 className="font-medium text-text-primary">{collaborator?.name}</h5>
                  <span className="text-xs bg-muted px-2 py-1 rounded">{collaborator?.role}</span>
                </div>
                <div className="flex items-center space-x-4 text-xs text-text-secondary">
                  <span>{getStatusText(collaborator?.status)}</span>
                  <span>•</span>
                  <span>Last active: {new Date(collaborator.lastActive)?.toLocaleDateString()}</span>
                </div>
                {collaborator?.sections?.length > 0 && (
                  <div className="flex items-center space-x-1 mt-1">
                    <Icon name="FileText" size={12} color="var(--color-text-secondary)" />
                    <span className="text-xs text-text-secondary">
                      Working on: {collaborator?.sections?.slice(0, 2)?.join(', ')}
                      {collaborator?.sections?.length > 2 && ` +${collaborator?.sections?.length - 2} more`}
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <div className="flex items-center space-x-1">
                {collaborator?.permissions?.includes('edit') && (
                  <Icon name="Edit" size={14} color="var(--color-success)" title="Can edit" />
                )}
                {collaborator?.permissions?.includes('review') && (
                  <Icon name="Eye" size={14} color="var(--color-primary)" title="Can review" />
                )}
                {collaborator?.permissions?.includes('export') && (
                  <Icon name="Download" size={14} color="var(--color-warning)" title="Can export" />
                )}
              </div>
              <Button variant="ghost" size="sm">
                <Icon name="MoreVertical" size={16} color="currentColor" />
              </Button>
            </div>
          </div>
        ))}
      </div>
      {/* Comments & Feedback */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-medium text-text-primary">
            Comments & Feedback ({unresolvedComments?.length} unresolved)
          </h4>
          <Button variant="outline" size="sm">
            <Icon name="MessageSquare" size={16} color="currentColor" className="mr-2" />
            Add Comment
          </Button>
        </div>

        <div className="space-y-3">
          {mockComments?.map((comment) => {
            const author = mockCollaborators?.find(c => c?.id === comment?.userId);
            return (
              <div
                key={comment?.id}
                className={`p-3 rounded-lg border ${comment?.resolved ? 'border-success/30 bg-success/5' : 'border-border bg-surface'}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3">
                    <img
                      src={author?.avatar}
                      alt={author?.name}
                      className="w-8 h-8 rounded-full object-cover"
                    />
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="font-medium text-text-primary text-sm">{author?.name}</span>
                        <span className="text-xs bg-muted px-2 py-1 rounded">{comment?.section}</span>
                        <span className="text-xs text-text-secondary">
                          {new Date(comment.timestamp)?.toLocaleDateString()}
                        </span>
                      </div>
                      <p className="text-sm text-text-secondary">{comment?.content}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {comment?.resolved ? (
                      <Icon name="CheckCircle" size={16} color="var(--color-success)" />
                    ) : (
                      <Button variant="ghost" size="sm">
                        <Icon name="Check" size={14} color="currentColor" />
                      </Button>
                    )}
                    <Button variant="ghost" size="sm">
                      <Icon name="Reply" size={14} color="currentColor" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {mockComments?.length === 0 && (
          <div className="text-center py-8">
            <Icon name="MessageSquare" size={48} color="var(--color-text-secondary)" className="mx-auto mb-4" />
            <h5 className="font-medium text-text-primary mb-2">No Comments Yet</h5>
            <p className="text-text-secondary text-sm">
              Team members can add comments and feedback here
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CollaborationPanel;